import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BookOpenIcon,
  CalendarDaysIcon,
  ClockIcon,
  HomeIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  UserMinusIcon,
} from "@heroicons/react/24/outline";
import { useGeneralNavMenuPageActionsState } from "./general-nav-menu-context";

const todayString = () => new Date().toISOString().substring(0, 10);

export function GeneralNavMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const pageActions = useGeneralNavMenuPageActionsState();
  const [sheetOpen, setSheetOpen] = useState(false);

  const goToTodayCalendar = () => {
    navigate(`/dashboard/calendar?view=day&date=${todayString()}`);
  };

  const pathname = location?.pathname || "";
  const isHome = pathname === "/dashboard" || pathname === "/dashboard/";
  const isTimesheet = pathname.startsWith("/dashboard/hours");
  const isCalendar = pathname.startsWith("/dashboard/calendar");
  const isHandbook = pathname.startsWith("/dashboard/quality/employee-handbook");

  const itemClass = (active) =>
    `flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 transition-colors active:bg-gray-100 ${
      active ? "bg-teal-50 text-teal-600" : "text-gray-600 hover:bg-gray-50"
    }`;

  const labelClass =
    "max-w-full truncate px-0.5 text-center text-[10px] font-medium leading-tight";

  const handleNewEvent = () => {
    setSheetOpen(false);
    if (pageActions?.onNewEvent) {
      pageActions.onNewEvent();
      return;
    }
    navigate("/dashboard/hse-ks/events/new");
  };

  const handleNewHour = () => {
    setSheetOpen(false);
    if (pageActions?.onNewHour) {
      pageActions.onNewHour();
      return;
    }
    navigate("/dashboard/hours");
  };

  const handleNewAbsence = () => {
    setSheetOpen(false);
    if (pageActions?.onNewAbsence) {
      pageActions.onNewAbsence();
      return;
    }
    navigate("/dashboard/absences/new");
  };

  return (
    <>
      {sheetOpen ? (
        <button
          type="button"
          className="lg:hidden fixed inset-0 z-40 bg-slate-950/40"
          aria-label={t("generalNavMenu.closeActions")}
          onClick={() => setSheetOpen(false)}
        />
      ) : null}

      {sheetOpen ? (
        <div
          className="lg:hidden fixed left-3 right-3 z-50 rounded-2xl border border-gray-200 bg-white p-2 shadow-lg"
          style={{
            bottom: "calc(4.75rem + env(safe-area-inset-bottom, 0px))",
          }}
          role="menu"
          aria-label={t("generalNavMenu.actionsTitle")}
        >
          <div className="px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">
            {t("generalNavMenu.actionsTitle")}
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={handleNewEvent}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-gray-900 transition hover:bg-gray-50"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-700">
              <ExclamationTriangleIcon className="h-5 w-5" aria-hidden />
            </span>
            {t("generalNavMenu.newEvent")}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={handleNewHour}
            className="mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-gray-900 transition hover:bg-gray-50"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50 text-teal-700">
              <ClockIcon className="h-5 w-5" aria-hidden />
            </span>
            {t("generalNavMenu.newHour")}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={handleNewAbsence}
            className="mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-gray-900 transition hover:bg-gray-50"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-50 text-sky-700">
              <UserMinusIcon className="h-5 w-5" aria-hidden />
            </span>
            {t("generalNavMenu.newAbsence")}
          </button>
        </div>
      ) : null}

      <div
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 pointer-events-none"
        style={{
          paddingBottom: "max(0.35rem, env(safe-area-inset-bottom, 0px))",
        }}
      >
        <div className="pointer-events-auto relative mx-auto max-w-lg">
          <button
            type="button"
            onClick={() => setSheetOpen((open) => !open)}
            className={`absolute left-1/2 top-0 z-50 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border shadow-lg transition ${
              sheetOpen
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-teal-600 bg-teal-600 text-white hover:bg-teal-700"
            }`}
            aria-label={t("generalNavMenu.openActions")}
            aria-expanded={sheetOpen}
            aria-haspopup="menu"
          >
            <PlusIcon className="h-7 w-7" aria-hidden />
          </button>

          <nav
            className="border-t border-gray-200 bg-white/95 shadow-[0_-4px_14px_rgba(0,0,0,0.06)] backdrop-blur-sm supports-[backdrop-filter]:bg-white/90"
            aria-label={t("generalNavMenu.ariaLabel")}
          >
            <div className="flex items-stretch justify-around gap-0 px-1 pb-1 pt-3">
              <button
                type="button"
                onClick={() => navigate("/dashboard/")}
                className={itemClass(isHome)}
              >
                <HomeIcon className="h-5 w-5 shrink-0" aria-hidden />
                <span className={labelClass}>{t("generalNavMenu.home")}</span>
              </button>

              <button
                type="button"
                onClick={() => navigate("/dashboard/hours")}
                className={itemClass(isTimesheet)}
              >
                <ClockIcon className="h-5 w-5 shrink-0" aria-hidden />
                <span className={labelClass}>{t("generalNavMenu.timesheet")}</span>
              </button>

              <div className="w-14 shrink-0" aria-hidden />

              <button
                type="button"
                onClick={goToTodayCalendar}
                className={itemClass(isCalendar)}
              >
                <CalendarDaysIcon className="h-5 w-5 shrink-0" aria-hidden />
                <span className={labelClass}>{t("generalNavMenu.calendar")}</span>
              </button>

              <button
                type="button"
                onClick={() => navigate("/dashboard/quality/employee-handbook")}
                className={itemClass(isHandbook)}
              >
                <BookOpenIcon className="h-5 w-5 shrink-0" aria-hidden />
                <span className={labelClass}>{t("generalNavMenu.handbook")}</span>
              </button>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
}

export default GeneralNavMenu;
